import LegalLayout, { Section } from "@/components/LegalLayout";
import { Mail, ShieldCheck, FileText } from "lucide-react";

const LAST_UPDATED = "27 February 2026";
const PREFIX = "deletion";

/**
 * Data Deletion Instructions — published per Google Play's "Account deletion
 * URL" requirement and used as the in-app deletion target on iOS (Apple
 * Guideline 5.1.1(v)). Same URL is referenced from the customer + driver app
 * Profile screens.
 */
export default function DataDeletionPage() {
  return (
    <LegalLayout
      title="Account &amp; Data Deletion"
      canonical="https://transportanything247.com/data-deletion"
      description="How to delete your Transport Anything 247 customer or driver account and the personal data associated with it."
      lastUpdated={LAST_UPDATED}
      testidPrefix={PREFIX}
    >
      <Section id="summary" title="At a glance" testidPrefix={PREFIX}>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <ul className="space-y-2">
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" /><span>You can delete your account at any time, free of charge.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" /><span>We confirm receipt within 1 working day and complete deletion within 30 days.</span></li>
            <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" /><span>Some records (invoices, POD photos, driver verification documents) are retained for 6 years to comply with HMRC and contract obligations — see Section 4 of our <a href="/privacy" className="text-orange-600 underline">Privacy Policy</a>.</span></li>
          </ul>
        </div>
      </Section>

      <Section id="how" title="How to request deletion" testidPrefix={PREFIX}>
        <p>Choose whichever route suits you.</p>

        <h3 className="text-base font-semibold text-slate-900 mt-4 mb-1 flex items-center gap-2">
          <Mail className="w-4 h-4 text-orange-600" /> Option 1 — Email (fastest)
        </h3>
        <p>
          Send an email from the address on the account to{" "}
          <a href="mailto:customers@transportanything247.com?subject=Account%20deletion%20request" className="text-orange-600 underline">
            customers@transportanything247.com
          </a>{" "}
          with the subject line "Account deletion request". Include your name and account email.
        </p>

        <h3 className="text-base font-semibold text-slate-900 mt-4 mb-1 flex items-center gap-2">
          <FileText className="w-4 h-4 text-orange-600" /> Option 2 — In-app
        </h3>
        <p>
          Customers: open the customer app → Profile → "Delete account".<br />
          Drivers: open the driver app → Profile → "Delete account".<br />
          You'll be asked to confirm; once confirmed we email a final confirmation and start the
          deletion process.
        </p>

        <h3 className="text-base font-semibold text-slate-900 mt-4 mb-1">Option 3 — Post</h3>
        <p>
          Write to us by post (we accept letters, but email is much faster):<br />
          <span className="text-slate-700">Data Protection, Transport Anything 247, United Kingdom.</span>
        </p>
      </Section>

      <Section id="what-we-delete" title="What is deleted" testidPrefix={PREFIX}>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your account profile (name, email, phone, hashed password).</li>
          <li>Stored Stripe identifiers (Stripe will retain your payment record separately under their own policy).</li>
          <li>Saved addresses, default vehicle preferences and any in-app settings.</li>
          <li>GPS breadcrumbs and aggregated mileage figures linked to your driver account.</li>
          <li>Chat messages tied to your account.</li>
          <li>Push notification tokens and subscriptions.</li>
        </ul>
      </Section>

      <Section id="what-we-keep" title="What is retained — and why" testidPrefix={PREFIX}>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Completed-booking invoices and receipts</strong> — retained 6 years to comply with HMRC record-keeping (Finance Act 2008).</li>
          <li><strong>Proof-of-delivery photos</strong> — retained 6 years as evidence of contract performance, in case of insurance claims or commercial disputes.</li>
          <li><strong>Driver verification documents</strong> — retained for the lifetime of the driver relationship plus 6 years (insurance + carrier-licensing audits).</li>
        </ul>
        <p>
          After the personal-data deletion step, these retained records are anonymised or pseudonymised
          so they are no longer linked to you individually.
        </p>
      </Section>

      <Section id="timeline" title="Timeline" testidPrefix={PREFIX}>
        <ol className="list-decimal pl-5 space-y-1">
          <li><strong>Within 1 working day:</strong> we acknowledge receipt and verify your identity.</li>
          <li><strong>Within 7 days:</strong> we disable sign-in to your account and stop all new dispatch / booking activity.</li>
          <li><strong>Within 30 days:</strong> we complete personal-data deletion across our systems and notify our processors (Stripe, Resend / email provider, Firebase / APNs) to revoke any retained tokens tied to your account.</li>
        </ol>
      </Section>

      <Section id="rights" title="Your rights" testidPrefix={PREFIX}>
        <p>
          Beyond deletion, you also have the right to: access the data we hold about you, correct
          inaccuracies, port your data to another service, restrict processing in certain
          circumstances, and complain to the UK Information Commissioner's Office. See Section 16 of
          our <a href="/privacy" className="text-orange-600 underline">Privacy Policy</a> for details.
        </p>
      </Section>

      <Section id="contact" title="Need help?" testidPrefix={PREFIX}>
        <div className="bg-slate-900 text-white rounded-xl p-5">
          <p className="text-sm leading-relaxed">
            Any difficulty with your deletion request:<br />
            <a href="mailto:customers@transportanything247.com" className="text-orange-400 underline font-semibold">
              customers@transportanything247.com
            </a><br />
            <span className="text-slate-300 text-xs">We respond within 1 working day.</span>
          </p>
        </div>
      </Section>
    </LegalLayout>
  );
}
